import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  sendResetLink,
  clearResetState,
} from "../../../store/slices/resetPasswordSlice"
import { getAuthMessage } from "../../../lib/constants/authStatusCodes"

const ForgotPasswordPopup = ({
  isOpen,
  onClose,
  defaultEmail = "",
  onSuccess,
  isValidEmail,
}) => {
  const dispatch = useDispatch()
  const resetError = useSelector((state) => state.resetPassword.error)

  const [forgotEmail, setForgotEmail] = useState(defaultEmail)
  const [forgotLocalError, setForgotLocalError] = useState("")

  useEffect(() => {
    setForgotEmail(defaultEmail || "")
  }, [defaultEmail, isOpen])

  if (!isOpen) return null

  const handleSendResetLink = async () => {
    if (!forgotEmail.trim()) {
      setForgotLocalError("Please enter your email")
      return
    }

    if (isValidEmail && !isValidEmail(forgotEmail)) {
      setForgotLocalError("Please enter a valid email address.")
      return
    }

    dispatch(clearResetState())

    const res = await dispatch(sendResetLink(forgotEmail))

    if (res.meta.requestStatus === "fulfilled") {
      const payload = res.payload

      const heading =
        typeof payload === "object" && payload !== null
          ? payload.message ||
            getAuthMessage(payload.code) ||
            "Password reset link sent"
          : payload || "Password reset link sent"

      onSuccess?.({
        heading,
        email: forgotEmail,
      })

      setForgotEmail("")
    }
  }

  return (
    <div className="forgot-slide-container">
      <div className="forgot-card">
        <button
          className="forgot-close"
          onClick={() => {
            setForgotEmail("")
            setForgotLocalError("")
            dispatch(clearResetState())
            onClose()
          }}
        >
          ✖
        </button>

        {/* Lock Icon */}
        <div className="lock-icon">
          <svg width="28" height="32" viewBox="0 0 28 32" fill="none">
            <path
              d="M9 9V12H19V9C19 6.2375 16.7625 4 14 4C11.2375 4 9 6.2375 9 9ZM5 12V9C5 4.03125 9.03125 0 14 0C18.9688 0 23 4.03125 23 9V12H24C26.2062 12 28 13.7937 28 16V28C28 30.2062 26.2062 32 24 32H4C1.79375 32 0 30.2062 0 28V16C0 13.7937 1.79375 12 4 12H5Z"
              fill="#4460F7"
            />
          </svg>
        </div>

        <h2 className="forgot-title">Forgot Password</h2>

        <p className="forgot-subtitle">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>

        <div className="forgot-form">
          <label className="forgot-label">Email address</label>

          <div className="showInputIcon">
            {/* Email Icon */}
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
              <path
                d="M2 1.5C1.725 1.5 1.5 1.725 1.5 2V2.69063L6.89062 7.11563C7.5375 7.64688 8.46562 7.64688 9.1125 7.11563L14.5 2.69063V2C14.5 1.725 14.275 1.5 14 1.5H2ZM1.5 4.63125V10C1.5 10.275 1.725 10.5 2 10.5H14C14.275 10.5 14.5 10.275 14.5 10V4.63125L10.0625 8.275C8.8625 9.25937 7.13438 9.25937 5.9375 8.275L1.5 4.63125ZM0 2C0 0.896875 0.896875 0 2 0H14C15.1031 0 16 0.896875 16 2V10C16 11.1031 15.1031 12 14 12H2C0.896875 12 0 11.1031 0 10V2Z"
                fill="#9CA3AF"
              />
            </svg>

            <input
              type="email"
              placeholder="your@email.com"
              className="forgot-input"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value)
                setForgotLocalError("")
              }}
            />
          </div>

          {(forgotLocalError || resetError) && (
            <p className="forgot-error-text">
              {forgotLocalError || getAuthMessage(resetError)}
            </p>
          )}

          <button className="div-33" onClick={handleSendResetLink}>
            <div className="div-34">
              {/* Send Icon */}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M16 16H0V0H16V16Z" stroke="0" />
                <g clipPath="url(#clip0_send)">
                  <path
                    d="M15.5655 0.175119C15.8811 0.393869 16.0467 0.771994 15.9873 1.15012L13.9873 14.1501C13.9405 14.4532 13.7561 14.7189 13.4873 14.8689C13.2186 15.0189 12.8967 15.0376 12.6123 14.9189L8.87484 13.3657L6.73421 15.6814C6.45609 15.9845 6.01859 16.0845 5.63421 15.9345C5.24984 15.7845 4.99984 15.4126 4.99984 15.0001V12.3876C4.99984 12.2626 5.04671 12.1439 5.13109 12.0532L10.3686 6.33762C10.5498 6.14074 10.5436 5.83762 10.3561 5.65012C10.1686 5.46262 9.86546 5.45012 9.66859 5.62824L3.31234 11.2751L0.552962 9.89387C0.221712 9.72824 0.00921221 9.39699 -0.000162795 9.02824C-0.0095378 8.65949 0.184212 8.31574 0.502962 8.13137L14.503 0.131369C14.8373 -0.0592555 15.2498 -0.0405055 15.5655 0.175119Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_send">
                    <path d="M0 0H16V16H0V0Z" fill="white" />
                  </clipPath>
                </defs>
              </svg>

              Send Reset Link
            </div>
          </button>

          <div
            className="forgot-back forgot-back-overlay"
            onClick={() => {
              dispatch(clearResetState())
              onClose()
            }}
          >
            <svg width="11" height="12" viewBox="0 0 11 12" fill="none">
              <path
                d="M0.220215 5.47207C-0.0727539 5.76504 -0.0727539 6.24082 0.220215 6.53379L3.97021 10.2838C4.26318 10.5768 4.73897 10.5768 5.03193 10.2838C5.3249 9.99082 5.3249 9.51504 5.03193 9.22207L2.55928 6.75176H9.7499C10.1647 6.75176 10.4999 6.4166 10.4999 6.00176C10.4999 5.58691 10.1647 5.25176 9.7499 5.25176H2.56162L5.02959 2.78145C5.32256 2.48848 5.32256 2.0127 5.02959 1.71973C4.73662 1.42676 4.26084 1.42676 3.96787 1.71973L0.217871 5.46973L0.220215 5.47207Z"
                fill="#2563EB"
              />
            </svg>

            Back to Sign in
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPopup